import React, { Component } from "react";
import PropTypes from "prop-types";
import Immutable from "immutable";
import { connect } from "react-redux";
import { translate } from "react-i18next";
import actions from "redux-form/lib/actions";
import formValueSelector from "redux-form/lib/formValueSelector";
import { createSelector } from "reselect";

import { createInitialRecipient } from "../../../store/modules/cart";

import StepBox from "../StepBox";

import "./RecipientContext.scss";

import RecipientDetails from "./RecipientDetails";
import ProductDetails from "./ProductDetails";
import DeliveryDetails from "./DeliveryDetails";
import CardDetails from "./CardDetails";
import AddressTypeServiceDatetime from "./AddressTypeServiceDatetime";

/**
 * RecipientContext
 */
class RecipientContext extends Component {
  static propTypes = {
    isActive: PropTypes.bool.isRequired,
    isValid: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired,
    step: PropTypes.number,
    canHaveExtraRecipients: PropTypes.bool.isRequired,
    recipientCount: PropTypes.number,
    onNextStep: PropTypes.func.isRequired,
    formArrayPush: PropTypes.func.isRequired,
    formArrayRemove: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    ctxIdx: PropTypes.number.isRequired,
    isLastRecipient: PropTypes.bool,
    iAmTheOneAndOnly: PropTypes.bool,
    hasOverview: PropTypes.bool,
    ctx: PropTypes.instanceOf(Immutable.Map).isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    items: PropTypes.array.isRequired,
    onForceValidate: PropTypes.func.isRequired,
    asyncValidate: PropTypes.func.isRequired
  };

  static childContextTypes = {
    ctxIdx: PropTypes.number,
    namePrefix: PropTypes.string,
    acPrefix: PropTypes.string
  };

  static defaultProps = {
    hasOverview: false,
    isLastRecipient: false,
    iAmTheOneAndOnly: true,
    step: null,
    recipientCount: 0
  };

  getChildContext() {
    return {
      ctxIdx: this.props.ctxIdx,
      namePrefix: `recipients[${this.props.ctxIdx}]`,
      acPrefix: `section-recipient${this.props.ctxIdx} shipping`
    };
  }

  shouldComponentUpdate(nextProps) {
    if (!this.props.ctx) {
      return true;
    }
    return (
      this.props.iAmTheOneAndOnly !== nextProps.iAmTheOneAndOnly ||
      this.props.isLastRecipient !== nextProps.isLastRecipient ||
      this.props.isOpen !== nextProps.isOpen ||
      this.props.isValid !== nextProps.isValid ||
      this.props.isActive !== nextProps.isActive ||
      !this.props.ctx.equals(nextProps.ctx)
    );
  }

  onClickAddExtraAddress = e => {
    e.preventDefault();
    this.props.onForceValidate();
    this.props.formArrayPush("cart", "recipients", {
      ...createInitialRecipient(),
      products: this.props.items.map(item => ({
        ...item,
        maxQuantity: item.quantity,
        quantity: 0
      }))
    });
  };

  onClickRemove = e => {
    e.preventDefault();
    this.props.onForceValidate();
    if (!this.props.iAmTheOneAndOnly) {
      this.props.formArrayRemove("cart", "recipients", this.props.ctxIdx);
    }
  };

  render() {
    const { ctx, ctxIdx, t, isActive, isValid, isOpen } = this.props;
    if (!ctx) {
      return null;
    }

    const title = t("recipientTitle", { count: ctxIdx + 1 });

    if (!ctx.get("availableDates")) {
      return (
        <StepBox
          number={this.props.step}
          title={title}
          isActive={false}
          isValid={isValid}
          isOpen={false}
          hasOverview={this.props.hasOverview}
        />
      );
    }
    return (
      <StepBox
        isActive={isActive}
        isValid={isValid}
        isOpen={isOpen}
        number={this.props.step}
        title={title}
        onNextStep={this.props.onNextStep}
        hasOverview={this.props.hasOverview}
        showButton={this.props.isLastRecipient}
      >
        {this.props.recipientCount > 1 && (
          <div>
            <button className="btn btn-inverted" type="button" onClick={this.onClickRemove}>
              {t("Remove this recipient")} <i className="fa fa-times" data-tip={t("Remove this recipient")} />
            </button>
          </div>
        )}
        <CardDetails ctxIdx={ctxIdx} ctx={ctx} />
        <div className="row">
          <RecipientDetails className="col-12 col-sm-6" ctxIdx={ctxIdx} ctx={ctx} />
          <div className="col-12 col-sm-6">
            <DeliveryDetails ctxIdx={ctxIdx} ctx={ctx} />
          </div>
        </div>
        {this.props.recipientCount > 1 && <ProductDetails ctxIdx={ctxIdx} ctx={ctx} />}

        <AddressTypeServiceDatetime ctxIdx={ctxIdx} ctx={ctx} />

        {this.props.canHaveExtraRecipients && (
          <div className="RecipientContext__AddExtraRecipientButton">
            <button className="btn btn-inverted mr-3" onClick={this.onClickAddExtraAddress}>
              {t("addExtraRecipient")}
            </button>
            <i className="fa fa-info-circle" data-tip={t("addExtraRecipient_info")} />
            <div className="RecipientContext__AddExtraRecipientButton__Or">{t("or")}</div>
          </div>
        )}
      </StepBox>
    );
  }
}

const cartFormValueSelector = formValueSelector("cart");

const selectItemCount = createSelector(
  [state => cartFormValueSelector(state, "items")],
  items =>
    items.reduce((carry, item) => {
      carry += item.quantity;
      return carry;
    }, 0)
);

const selectCanHaveExtraRecipients = createSelector(
  [state => selectItemCount(state), (state, props) => props.isLastRecipient, (state, props) => props.ctxIdx],
  itemCount => itemCount > 1
);

export default translate()(
  connect(
    (state, props) => ({
      items: cartFormValueSelector(state, "items"),
      canHaveExtraRecipients: selectCanHaveExtraRecipients(state, props)
    }),
    {
      formArrayPush: actions.arrayPush,
      formArrayRemove: actions.arrayRemove
    }
  )(RecipientContext)
);
